/*
 * The promoted shortlist: which articles the public site shows as tiles above
 * the article list, and in what order.
 *
 * The page edits a LOCAL copy and saves the whole list at once - matching the
 * API's replace-all contract - so reordering is instant, mistakes are
 * revertible until Save, and two half-saved states cannot exist. Reordering
 * has two inputs on purpose: drag-and-drop for the pointer, and up/down
 * buttons - which stay because HTML5 drag events simply do not exist for
 * keyboards or most touch input, so removing the buttons would remove the
 * accessible path.
 *
 * Promotion is not publishing. An entry whose article is hidden, scheduled or
 * unwritten stays ON this list but is marked "not shown" - the public view
 * reads through kb.PromotedArticlePublished and drops it. Hiding such entries
 * here instead would make the page lie about what is stored.
 *
 * Its words come from the chosen language's dictionary, `t.admin.site` (S48).
 */

import { useEffect, useMemo, useState } from 'react';
import type { AdminArticleListItem, PromotedArticleEntry } from '@accotool-kb/shared';
import { MAX_PROMOTED_ARTICLES } from '@accotool-kb/shared';
import { useAdminApi } from '../auth/AuthProvider.js';
import { reorderList } from './reorderList.js';
import { useAction, useAsync, useDebounced } from '../hooks/useAsync.js';
import { ErrorBanner, Loading, Tag } from '../components/ui.js';
import { useMessages } from '../i18n/language.js';

export function PromotedArticlesPage(): JSX.Element {
  const s = useMessages().admin.site.promoted;
  const api = useAdminApi();
  const state = useAsync(() => api.listPromoted(), []);

  // The working copy. Seeded from the load, then owned by the page until Save.
  const [items, setItems] = useState<PromotedArticleEntry[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Read by the aria-live region: screen-reader confirmation of each reorder,
  // which the visual position numbers communicate silently.
  const [announcement, setAnnouncement] = useState('');
  // Index being dragged, and the index the pointer is currently over - the
  // second drives the insertion indicator.
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const save = useAction();

  useEffect(() => {
    if (state.data && items === null) setItems(state.data);
  }, [state.data, items]);

  const dirty = useMemo(() => {
    if (!state.data || !items) return false;
    return (
      items.length !== state.data.length ||
      items.some((item, index) => item.articleId !== state.data?.[index]?.articleId)
    );
  }, [items, state.data]);

  const moveTo = (from: number, to: number) => {
    setItems((current) => {
      if (!current) return current;
      const moving = current[from];
      if (!moving) return current;
      const clamped = Math.max(0, Math.min(current.length - 1, to));
      const next = reorderList(current, from, clamped);
      if (next === current) {
        setAnnouncement(to < from ? s.alreadyFirst(moving.title) : s.alreadyLast(moving.title));
        return current;
      }
      setAnnouncement(s.moved(moving.title, clamped + 1, next.length));
      return [...next];
    });
  };

  const move = (index: number, delta: -1 | 1) => moveTo(index, index + delta);

  const dropOn = (targetIndex: number) => {
    if (dragIndex !== null) moveTo(dragIndex, targetIndex);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const remove = (articleId: string) => {
    setItems((current) => current?.filter((item) => item.articleId !== articleId) ?? current);
  };

  const add = (article: AdminArticleListItem) => {
    setItems((current) => {
      if (!current || current.length >= MAX_PROMOTED_ARTICLES) return current;
      if (current.some((item) => item.articleId === article.articleId)) return current;
      return [
        ...current,
        {
          articleId: article.articleId,
          sortOrder: current.length + 1,
          title: article.title ?? article.slug,
          slug: article.slug,
          isLive: article.status === 'live',
          product: article.product,
          // Names only: that is the shape a shortlist entry carries, and the
          // reason is on the type. Taking them from the picker's own rows keeps
          // the tags on screen from the moment Add is clicked - waiting for the
          // save to answer would blank them for the length of a round trip, on
          // the one row the admin is looking at.
          categoryTags: article.categories.map((category) => category.name),
        },
      ];
    });
  };

  const onSave = async () => {
    if (!items) return;
    const stored = await save.run(() => api.setPromoted(items.map((item) => item.articleId)));
    if (stored) {
      // The PUT already returned the stored list; re-fetching it would only
      // add a failure window in which a SUCCESSFUL save renders as an error.
      setItems(stored);
      state.setData(stored);
      setNotice(s.saved);
    }
  };

  if (state.loading && !state.data) return <Loading label={s.loading} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!items) return <Loading />;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{s.title}</h1>
          <p>{s.intro(MAX_PROMOTED_ARTICLES)}</p>
        </div>
        <button
          type="button"
          className="button button-primary"
          disabled={!dirty || save.running}
          onClick={() => void onSave()}
        >
          {save.running ? s.saving : s.saveOrder}
        </button>
      </div>

      <p className="visually-hidden" aria-live="polite">
        {announcement}
      </p>

      {save.error && <ErrorBanner error={save.error} onRetry={() => void onSave()} />}
      {notice && !dirty && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}

      <div className="card">
        {items.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            {s.empty}
          </p>
        ) : (
          <ol className="promoted-admin-list">
            {items.map((item, index) => (
              <li
                key={item.articleId}
                className={`promoted-admin-row promoted-admin-draggable${
                  dragOverIndex === index && dragIndex !== index ? ' drag-over' : ''
                }${dragIndex === index ? ' dragging' : ''}`}
                draggable
                onDragStart={(event) => {
                  // Firefox refuses to start a drag without data on the transfer.
                  event.dataTransfer.setData('text/plain', String(index));
                  event.dataTransfer.effectAllowed = 'move';
                  setDragIndex(index);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  if (dragOverIndex !== index) setDragOverIndex(index);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  dropOn(index);
                }}
                onDragEnd={() => {
                  setDragIndex(null);
                  setDragOverIndex(null);
                }}
              >
                <span className="promoted-admin-grip" aria-hidden="true">
                  {'\u22ee\u22ee'}
                </span>
                <span className="promoted-admin-position">{index + 1}</span>
                <span className="promoted-admin-title">{item.title}</span>
                {!item.isLive && <Tag kind="neutral">{s.notShown}</Tag>}
                {/*
                  The same tags, in the same order, as the picker below: the
                  categories, then the one product. The shortlist is five titles
                  out of a hundred articles, and two products can hold the same
                  title, so the row has to say which article is actually on the
                  front page - the same argument that put them in the picker, and
                  it applies at least as strongly here, where the choice has
                  already been made and is being reviewed.
                */}
                <span className="tag-list promoted-admin-tags">
                  {item.categoryTags.map((tag) => (
                    <Tag key={`c-${tag}`} kind="category">
                      {tag}
                    </Tag>
                  ))}
                  <Tag kind="product">{item.product.name}</Tag>
                </span>
                <span className="promoted-admin-actions">
                  <button
                    type="button"
                    className="button button-quiet"
                    aria-label={s.moveUp(item.title)}
                    onClick={() => move(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="button button-quiet"
                    aria-label={s.moveDown(item.title)}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="button button-quiet"
                    aria-label={s.removeFrom(item.title)}
                    onClick={() => remove(item.articleId)}
                  >
                    {s.remove}
                  </button>
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>

      <AddArticlePanel
        disabled={items.length >= MAX_PROMOTED_ARTICLES}
        excludeIds={items.map((item) => item.articleId)}
        onAdd={add}
      />
    </>
  );
}

/**
 * Search-and-add, inline rather than a dialog: selection is this page's whole
 * job, so hiding the selection behind a modal would just add a click.
 */
function AddArticlePanel({
  disabled,
  excludeIds,
  onAdd,
}: {
  disabled: boolean;
  excludeIds: string[];
  onAdd: (article: AdminArticleListItem) => void;
}): JSX.Element {
  const s = useMessages().admin.site.promoted;
  const api = useAdminApi();
  const [draft, setDraft] = useState('');
  const q = useDebounced(draft, 350);

  const results = useAsync(
    (signal) =>
      api.listArticles(
        { q, products: [], categories: [], status: 'all', page: 1, pageSize: 20, sort: 'modified' },
        signal,
      ),
    [q],
  );

  const candidates = (results.data?.items ?? []).filter(
    (article) => !excludeIds.includes(article.articleId),
  );

  return (
    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
      <h2>{s.addTitle}</h2>
      {disabled ? (
        <p className="muted" style={{ margin: 0 }}>
          {s.full(MAX_PROMOTED_ARTICLES)}
        </p>
      ) : (
        <>
          <label htmlFor="promoted-search" className="visually-hidden">
            {s.searchLabel}
          </label>
          <input
            id="promoted-search"
            type="search"
            placeholder={s.searchPlaceholder}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          {results.error && <ErrorBanner error={results.error} onRetry={results.reload} />}
          {results.loading && !results.data && <Loading label={s.searching} />}
          {results.data && (
            <ul className="promoted-admin-list" style={{ marginTop: 'var(--space-3)' }}>
              {candidates.length === 0 && (
                <li className="muted">{s.noCandidates}</li>
              )}
              {candidates.map((article) => (
                <li key={article.articleId} className="promoted-admin-row">
                  <span className="promoted-admin-title">{article.title ?? article.slug}</span>
                  {article.status !== 'live' && (
                    <Tag kind="neutral">{s.status(article.status)}</Tag>
                  )}
                  {/*
                    The same tags the article list shows, in the same colours: the
                    categories, then the one product - kb.Article.ProductId is NOT
                    NULL, so there is no empty case and a row always ends on a
                    product tag.

                    THE ORDER IS DELIBERATELY THIS PAGE'S OWN. Everywhere else -
                    the article list, the public article header and the browse
                    cards - the product comes FIRST. Here the tags are right-
                    aligned against the Add button rather than left-aligned under a
                    title, so the end of the list is the edge the eye arrives at,
                    and the product is what belongs there. Do not "restore
                    consistency" by flipping this back; the two layouts are
                    mirror images and the same order cannot read the same way in
                    both.

                    The categories are printed in the order they arrive. Sorting
                    them here would be a second opinion about an order
                    kb.ArticleDetail has already settled.

                    They earn their place at all because titles alone do not
                    identify an article any more. Slugs are unique only within a
                    product, so two products can carry "Getting started", and this
                    search returns both; the product tag is what tells them apart
                    before promoting one of them to the front page.

                    No "needs a category" marker, unlike the article list. That is a
                    call to go and fix something, and this panel is a picker - the
                    list page is where the fixing happens.
                  */}
                  <span className="tag-list promoted-admin-tags">
                    {article.categories.map((category) => (
                      <Tag key={`c-${category.categoryId}`} kind="category">
                        {category.name}
                      </Tag>
                    ))}
                    <Tag kind="product">{article.product.name}</Tag>
                  </span>
                  <span className="promoted-admin-actions">
                    <button
                      type="button"
                      className="button button-quiet"
                      onClick={() => onAdd(article)}
                    >
                      {s.add}
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
